import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  KbSeverityBadge,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { getSupabase } from '../lib/supabase';

interface Entry {
  id: string;
  slug: string;
  type: string;
  severity: 'standard' | 'recommended' | 'optional';
  title: string;
  summary: string;
  content: string;
  rationale: string | null;
  code_example: string | null;
}

interface LoaderData {
  entry: Entry | null;
  tags: string[];
}

export async function kbReadLoader({ params }: LoaderFunctionArgs): Promise<LoaderData> {
  if (!params.slug) throw new Response('Not Found', { status: 404 });
  const res = await getApi().kbSearch({ query: `slug:${params.slug}` });
  if (res.mode !== 'read') return { entry: null, tags: [] };
  const entry = res.entry;
  if (!entry) return { entry: null, tags: [] };
  const { data: tags } = await getSupabase()
    .from('kb_tags')
    .select('tag')
    .eq('entry_id', entry.id);
  return { entry, tags: (tags ?? []).map((t) => t.tag) };
}

export function KbReadPage() {
  const { entry, tags } = useLoaderData() as LoaderData;

  if (!entry) {
    return (
      <EmptyState
        title="Entry not found"
        description="Check the slug; it may have been deprecated."
        action={
          <Button asChild variant="outline">
            <Link to="/knowledge">← Knowledge</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">{entry.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{entry.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{entry.type}</Badge>
            <KbSeverityBadge severity={entry.severity} />
            <code className="rounded bg-muted px-2 py-0.5 text-xs">{entry.slug}</code>
            {tags.map((t) => (
              <Badge key={t} variant="secondary">{t}</Badge>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to={`/knowledge/${entry.id}/edit`}>Edit</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/knowledge">← Knowledge</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Content</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{entry.content}</pre>
        </CardContent>
      </Card>

      {entry.rationale ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rationale</CardTitle>
            <CardDescription>Why this entry exists.</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{entry.rationale}</pre>
          </CardContent>
        </Card>
      ) : null}

      {entry.code_example ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md bg-muted p-4 text-xs">{entry.code_example}</pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
